"use client";

import { useId, useState, type MouseEvent, type ReactNode } from "react";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  MessageCircle,
  Pencil,
  Sparkles,
  WandSparkles,
} from "lucide-react";
import "./mobile-attention-panel.css";

export type MobileAttentionKind =
  | "idle"
  | "editing"
  | "thinking"
  | "generating"
  | "ready"
  | "next-step"
  | "error";

export interface MobileAttentionAction {
  id: string;
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

/** Status and the current assistant widget are supplied by the Create workflow. */
export interface MobileAttentionViewModel {
  kind: MobileAttentionKind;
  label: string;
  /** A new assistant turn closes the preceding turn's open choices. */
  identity?: string;
  detail?: string;
  primaryAction?: MobileAttentionAction;
  /** Active work can open its conversation without adding another CTA row. */
  onOpenChat?: () => void;
  /** The current assistant's real widget renderer, with its original handlers. */
  content?: ReactNode;
}

function AttentionIcon({ kind }: { kind: MobileAttentionKind }) {
  if (kind === "thinking") {
    return (
      <span className="mobile-attention-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    );
  }
  if (kind === "generating") {
    return <span className="mobile-attention-spinner" aria-hidden="true" />;
  }
  const Icon =
    kind === "error"
      ? AlertCircle
      : kind === "editing"
        ? Pencil
        : kind === "idle"
          ? MessageCircle
          : kind === "ready"
            ? WandSparkles
            : Sparkles;
  return <Icon size={17} strokeWidth={1.8} aria-hidden="true" />;
}

/** One compact row above the mobile composer; details and choices open on demand. */
export default function MobileAttentionPanel({
  model,
}: {
  model: MobileAttentionViewModel;
}) {
  const detailsId = useId();
  const detail = model.detail?.trim();
  const canExpand = Boolean(model.content || detail);
  const identity = `${model.identity ?? ""}:${model.kind}:${model.label}:${detail ?? ""}`;
  const [disclosure, setDisclosure] = useState({ identity, expanded: false });
  if (disclosure.identity !== identity) {
    // A returning status is a new moment, not permission to reopen an old panel.
    setDisclosure({ identity, expanded: false });
  }
  const expanded =
    canExpand && disclosure.identity === identity && disclosure.expanded;
  const status = (
    <>
      <span className="mobile-attention-icon" aria-hidden="true">
        <AttentionIcon kind={model.kind} />
      </span>
      <span
        key={identity}
        className="mobile-attention-label"
        role={model.kind === "error" ? "alert" : "status"}
        aria-live={model.kind === "error" ? "assertive" : "polite"}
      >
        {model.label}
      </span>
    </>
  );

  return (
    <section
      className="mobile-attention-panel"
      data-slot="mobile-attention-panel"
      data-mobile-attention-kind={model.kind}
      data-state={expanded ? "expanded" : "collapsed"}
      aria-label="Designer update"
    >
      <div className="mobile-attention-row">
        {canExpand ? (
          <button
            type="button"
            className="mobile-attention-disclosure"
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={() => setDisclosure({ identity, expanded: !expanded })}
          >
            {status}
            <ChevronDown
              className="mobile-attention-chevron"
              size={16}
              aria-hidden="true"
            />
          </button>
        ) : model.onOpenChat ? (
          <button
            type="button"
            className="mobile-attention-disclosure"
            aria-label={`View chat: ${model.label}`}
            onClick={model.onOpenChat}
          >
            {status}
            <ChevronRight
              className="mobile-attention-open-chat"
              size={16}
              aria-hidden="true"
            />
          </button>
        ) : (
          <div className="mobile-attention-static">{status}</div>
        )}
        {model.primaryAction ? (
          <button
            type="button"
            className="mobile-attention-primary"
            data-slot="mobile-attention-primary"
            disabled={model.primaryAction.disabled}
            onClick={model.primaryAction.onClick}
          >
            <span className="mobile-attention-primary-label">
              {model.primaryAction.label}
            </span>
          </button>
        ) : null}
      </div>
      {canExpand ? (
        <div
          id={detailsId}
          className="mobile-attention-details"
          data-slot="mobile-attention-details"
          data-state={expanded ? "expanded" : "collapsed"}
          aria-hidden={!expanded}
          inert={!expanded}
        >
          {detail ? <p className="mobile-attention-detail">{detail}</p> : null}
          {model.content ? (
            <div
              className="mobile-attention-content"
              data-slot="mobile-attention-content"
            >
              {model.content}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
